import { isAxiosError } from 'axios';
import { CommonError } from '../error';
import { debug } from '../logger';
import { UploadErrCode } from '../types';
import type { ErrCode } from '../types';
import type { AnyError } from 'zf-types';
import type { AxiosResponse } from 'axios';
import type { APIResponseError } from './types';
import type { UploadErrors } from './uploader/types';

export class ErrorParser {

  /**
   * 处理正常响应中的异常（返回码为200，但是body中包含异常信息）
   * @param response
   * @returns
   */
  parseError(response: AxiosResponse): void {
    const error = this.getError(response);
    if (error == null) {
      return;
    }

    // 先确认errors里的异常（后台有多个异常信息时，会放到error.errors里）
    if (error.errors != null && error.errors.length > 0) {
      throw new CommonError(error.errors?.join(','), UploadErrCode.UPLOAD_ERROR);
    }

    // 如果响应中包含异常信息，抛出
    throw new CommonError(error.message, error.code as ErrCode);
  }

  /**
   * 处理请求异常
   * @param error
   */
  parseRequestError(error: AnyError): void {
    debug.infra('request error', error);
    if (error.message != null && error.code != null) {
      throw new CommonError(error.message, error.code as ErrCode);
    }
    throw error;
  }

  /**
   * 处理响应异常
   * @param error
   */
  parseResponseError(error: unknown): void {
    debug.infra('response error', error);

    if (CommonError.isCommonError(error)) {
      debug.infra('common error', error);
      throw error;
    }

    // 未知异常（通常不会有这种情况）
    if (!isAxiosError(error)) {
      debug.infra('unknown error');
      throw error;
    }

    // 响应异常中无响应信息，直接抛出（通常不会有这种情况）
    if (error.response == null) {
      debug.infra('response empty error');
      throw error;
    }

    // 先尝试处理响应异常中的业务异常（即异常body中的详细信息）
    const detail = this.getError(error.response);
    if (detail != null) {
      // 先确认errors里的异常（后台有多个异常信息时，会放到error.errors里）
      // TODO: 这样处理 detail.message 会丢失，需要跟后台异常处理一起梳理
      if (detail.errors != null && detail.errors.length > 0) {
        throw new CommonError(detail.errors[0].message, detail.code as ErrCode);
      }

      // 普通的后台异常
      if (detail.message !== '') {
        throw new CommonError(detail.message, detail.code as ErrCode);
      }
    }

    // 处理504等后台响应异常
    if (error.response.status != null) {
      throw new CommonError(error.response.data as string, error.response.status as ErrCode);
    }

    // 其他未知异常
    throw error;
  }

  parseUploadError(result: unknown): void {
    if (this.isUploadError(result)) {
      throw new CommonError(result.errors?.join(','), UploadErrCode.UPLOAD_ERROR);
    }
  }

  private isUploadError(result: unknown): result is UploadErrors {
    return result != null && (result as UploadErrors).errors != null;
  }

  private getError(response: AxiosResponse): APIResponseError | undefined {
    if (response.data == null) {
      return;
    }
    return response.data.error;
  }

}
